import React, { useEffect, useState } from 'react';
import { promptService, SystemPrompt, CreatePromptData } from '../services/promptService';
import { FileText, Plus, Pencil, Trash2, Save, X, Loader2, AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const Prompts: React.FC = () => {
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [isPromptsLoading, setIsPromptsLoading] = useState(false);
  const [promptsError, setPromptsError] = useState<string | null>(null);
  const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState<number | null>(null);
  const [promptFormData, setPromptFormData] = useState<CreatePromptData>({
    prompt: '',
    model_name: 'gpt-4',
    description: ''
  });
  const [promptSuccess, setPromptSuccess] = useState(false);
  const [collapsedPrompts, setCollapsedPrompts] = useState<Set<number>>(new Set());
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [selectedPrompts, setSelectedPrompts] = useState<Set<number>>(new Set());
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);

  // Fetch prompts
  const fetchPrompts = async () => {
    try {
      setIsPromptsLoading(true);
      setPromptsError(null);
      const data = await promptService.getOrganizationPrompts();
      setPrompts(data);
      // Set all prompts as collapsed by default
      setCollapsedPrompts(new Set(data.map(p => p.id)));
    } catch (err: any) {
      setPromptsError(err.message || "Failed to load prompts");
    } finally {
      setIsPromptsLoading(false);
    }
  };

  // Create or update prompt
  const handleSavePrompt = async () => {
    if (!promptFormData.prompt.trim()) {
      setPromptsError("Prompt text is required");
      return;
    }

    try {
      if (editingPromptId) {
        await promptService.updateOrganizationPrompt(editingPromptId, promptFormData);
      } else {
        await promptService.createOrganizationPrompt(promptFormData);
      }

      setPromptFormData({ prompt: '', model_name: 'gpt-4', description: '' });
      setEditingPromptId(null);
      setIsCreatingPrompt(false);
      await fetchPrompts();
      setPromptSuccess(true);
      setTimeout(() => setPromptSuccess(false), 3000);
    } catch (err: any) {
      setPromptsError(err.message || "Failed to save prompt");
    }
  };

  // Delete prompt
  const handleDeletePrompt = async (promptId: number) => {
    if (!window.confirm("Are you sure you want to delete this prompt?")) {
      return;
    }

    try {
      await promptService.deleteOrganizationPrompt(promptId);
      await fetchPrompts();
      setPromptSuccess(true);
      setTimeout(() => setPromptSuccess(false), 3000);
    } catch (err: any) {
      setPromptsError(err.message || "Failed to delete prompt");
    }
  };

  // Toggle prompt selection
  const togglePromptSelection = (promptId: number) => {
    setSelectedPrompts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  // Select all prompts
  const selectAllPrompts = () => {
    if (selectedPrompts.size === prompts.length) {
      setSelectedPrompts(new Set());
    } else {
      setSelectedPrompts(new Set(prompts.map(p => p.id)));
    }
  };

  // Delete multiple prompts in sequence
  const handleDeleteMultiplePrompts = async () => {
    if (selectedPrompts.size === 0) {
      setPromptsError("Please select at least one prompt to delete");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedPrompts.size} prompt(s)? This action cannot be undone.`)) {
      return;
    }

    setIsDeletingMultiple(true);
    setDeleteProgress(0);
    setUploadStatus('Starting deletion...');

    let successCount = 0;
    let errorCount = 0;
    const selectedArray = Array.from(selectedPrompts);

    for (let i = 0; i < selectedArray.length; i++) {
      const promptId = selectedArray[i];
      const prompt = prompts.find(p => p.id === promptId);
      setUploadStatus(`Deleting ${i + 1}/${selectedArray.length}: ${prompt?.description || `Prompt ${promptId}`}...`);

      try {
        await promptService.deleteOrganizationPrompt(promptId);
        successCount++;
        console.log(`✅ Successfully deleted prompt: ${promptId}`);
      } catch (error: any) {
        console.error(`❌ Failed to delete prompt ${promptId}:`, error.message);
        errorCount++;
      }

      setDeleteProgress(((i + 1) / selectedArray.length) * 100);

      // Small delay between deletions to avoid rate limiting
      if (i < selectedArray.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setUploadStatus(`Complete! ${successCount} deleted, ${errorCount} failed`);
    setIsDeletingMultiple(false);
    setSelectedPrompts(new Set());

    // Refresh prompts list
    await fetchPrompts();

    // Show success message
    setPromptSuccess(true);
    setTimeout(() => {
      setPromptSuccess(false);
      setUploadStatus('');
      setDeleteProgress(0);
    }, 5000);
  };

  // Edit prompt
  const handleEditPrompt = (prompt: SystemPrompt) => {
    setEditingPromptId(prompt.id);
    setPromptFormData({
      prompt: prompt.prompt,
      model_name: prompt.model_name,
      description: prompt.description
    });
    setIsCreatingPrompt(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Toggle prompt active status
  const handleTogglePromptActive = async (promptId: number, isActive: boolean) => {
    try {
      await promptService.updateOrganizationPrompt(promptId, { is_active: !isActive });
      await fetchPrompts();
    } catch (err: any) {
      setPromptsError(err.message || "Failed to update prompt status");
    }
  };

  // Toggle individual prompt collapse
  const togglePromptCollapse = (promptId: number) => {
    setCollapsedPrompts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  // Load prompts on mount
  useEffect(() => {
    fetchPrompts();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Prompts</h1>
              <p className="text-gray-600 mt-2">
                Create and manage organization-specific system prompts for AI models
              </p>
            </div>
            {!isCreatingPrompt && (
              <button
                onClick={() => {
                  setIsCreatingPrompt(true);
                  setEditingPromptId(null);
                  setPromptFormData({ prompt: '', model_name: 'gpt-4', description: '' });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Plus className="h-5 w-5" />
                New Prompt
              </button>
            )}
          </div>
        </div>

        {/* Success Message */}
        {promptSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800 font-medium">
              {uploadStatus || 'Prompt saved successfully!'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {promptsError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{promptsError}</p>
            </div>
            <button
              onClick={() => setPromptsError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Create/Edit Form */}
        {isCreatingPrompt && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingPromptId ? 'Edit Prompt' : 'Create New Prompt'}
                </h2>
                <button
                  onClick={() => {
                    setIsCreatingPrompt(false);
                    setEditingPromptId(null);
                    setPromptFormData({ prompt: '', model_name: 'gpt-4', description: '' });
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="px-6 py-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt Text <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={promptFormData.prompt}
                  onChange={(e) => setPromptFormData({ ...promptFormData, prompt: e.target.value })}
                  className="block w-full rounded-lg border border-gray-300 shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900"
                  rows={8}
                  placeholder="Enter your system prompt here..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={promptFormData.model_name}
                    onChange={(e) => setPromptFormData({ ...promptFormData, model_name: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900"
                    placeholder="gpt-4"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={promptFormData.description}
                    onChange={(e) => setPromptFormData({ ...promptFormData, description: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900"
                    placeholder="Brief description of this prompt"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSavePrompt}
                  className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
                >
                  <Save className="h-4 w-4" />
                  {editingPromptId ? 'Update Prompt' : 'Create Prompt'}
                </button>
                <button
                  onClick={() => {
                    setIsCreatingPrompt(false);
                    setEditingPromptId(null);
                    setPromptFormData({ prompt: '', model_name: 'gpt-4', description: '' });
                  }}
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Progress */}
        {isDeletingMultiple && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="h-5 w-5 text-red-600 flex-shrink-0 animate-spin" />
              <p className="text-sm text-red-800 font-medium">{uploadStatus}</p>
            </div>
            <div className="w-full bg-red-200 rounded-full h-2">
              <div
                className="bg-red-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${deleteProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Prompts List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">All Prompts</h2>
              {prompts.length > 0 && (
                <div className="flex items-center gap-3">
                  {selectedPrompts.size > 0 && (
                    <>
                      <span className="text-sm text-gray-600">
                        {selectedPrompts.size} selected
                      </span>
                      <button
                        onClick={handleDeleteMultiplePrompts}
                        disabled={isDeletingMultiple}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors shadow-sm ${
                          isDeletingMultiple
                            ? 'bg-red-400 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        {isDeletingMultiple ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4" />
                            Delete Selected
                          </>
                        )}
                      </button>
                    </>
                  )}
                  <button
                    onClick={selectAllPrompts}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {selectedPrompts.size === prompts.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            {isPromptsLoading ? (
              <div className="flex justify-center py-16">
                <div className="flex flex-col items-center space-y-4">
                  <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                  <span className="text-sm text-gray-500">Loading prompts...</span>
                </div>
              </div>
            ) : prompts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FileText className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No prompts yet</h3>
                <p className="text-sm text-gray-500 max-w-md mb-6">
                  Create your first organization-specific system prompt to get started.
                </p>
                {!isCreatingPrompt && (
                  <button
                    onClick={() => {
                      setIsCreatingPrompt(true);
                      setEditingPromptId(null);
                      setPromptFormData({ prompt: '', model_name: 'gpt-4', description: '' });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Create Your First Prompt
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {prompts.map((prompt) => {
                  const isCollapsed = collapsedPrompts.has(prompt.id);
                  const isSelected = selectedPrompts.has(prompt.id);
                  return (
                  <div
                    key={prompt.id}
                    className={`border rounded-lg hover:shadow-sm transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              togglePromptSelection(prompt.id);
                            }}
                            className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          />
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => togglePromptCollapse(prompt.id)}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-gray-900">
                                {prompt.description || 'Untitled Prompt'}
                              </h3>
                              <span className="text-sm text-gray-500">
                                {prompt.model_name}
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-mono">
                                ID: {prompt.id}
                              </span>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                prompt.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {prompt.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPrompt(prompt);
                            }}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePrompt(prompt.id);
                            }}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePromptCollapse(prompt.id);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                            title={isCollapsed ? "Expand" : "Collapse"}
                          >
                            {isCollapsed ? (
                              <ChevronDown className="h-5 w-5" />
                            ) : (
                              <ChevronUp className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {!isCollapsed && (
                      <div className="px-6 pb-6 space-y-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                            {prompt.prompt}
                          </pre>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">
                            Created: {new Date(prompt.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                          <button
                            onClick={() => handleTogglePromptActive(prompt.id, prompt.is_active)}
                            className="px-4 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg font-medium transition-colors"
                          >
                            {prompt.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prompts;
